import { Chassis } from '../../scene/Chassis';
import { useBodyPalette } from '../../scene/BodyFinish';
import { GLASS, HEADLIGHT } from '../../scene/materials';
import { beamBetween } from '../geometry/panels';

const Box = ({ args, material, position, rotation }) => <mesh position={position} rotation={rotation}>
  <boxGeometry args={args} />
  <meshPhysicalMaterial {...material} />
</mesh>;

const Cylinder = ({ args, material, position, rotation = [Math.PI / 2, 0, 0] }) => (
  <mesh position={position} rotation={rotation}>
    <cylinderGeometry args={args} />
    <meshPhysicalMaterial {...material} />
  </mesh>
);

const Cab = ({ palette, position = [-1.25, 1.65, 0], size = [1.45, 1.45, 1.45] }) => <group>
  <Box args={size} material={palette.body} position={position} />
  <Box
    args={[size[0] * 0.72, size[1] * 0.52, size[2] * 1.01]}
    material={GLASS}
    position={[position[0] + (size[0] * 0.08), position[1] + (size[1] * 0.13), position[2]]}
  />
  <Box
    args={[0.09, 0.19, 0.48]}
    material={HEADLIGHT}
    position={[position[0] + (size[0] / 2) + 0.05, position[1] - 0.25, position[2]]}
  />
</group>;

const Boom = ({ palette, from, to, thickness = 0.18, depth = 0.22, z = 0 }) => {
  const beam = beamBetween(from, to, { thickness, depth, z });
  return <Box args={beam.args} material={palette.body} position={beam.position} rotation={beam.rotation} />;
};

const StandardFrame = ({ palette, length = 5.2, width = 1.35 }) => <Chassis>
  <Box args={[length, 0.34, width]} material={palette.chassis} position={[0, 0.76, 0]} />
</Chassis>;

const FuelBowserBody = () => {
  const palette = useBodyPalette('fuel_bowser');
  return <group>
    <StandardFrame palette={palette} length={7.2} />
    <Cab palette={palette} position={[2.35, 1.58, 0]} />
    <Cylinder args={[0.9, 0.9, 3.8, 28]} material={palette.secondary} position={[-1.05, 1.55, 0]} rotation={[0, 0, Math.PI / 2]} />
    <Box args={[0.5, 0.42, 1.28]} material={palette.edge} position={[-3.05, 0.98, 0]} />
  </group>;
};

const ServiceVehicleBody = () => {
  const palette = useBodyPalette('service_vehicle');
  return <group>
    <StandardFrame palette={palette} />
    <Cab palette={palette} position={[1.25, 1.55, 0]} />
    <Box args={[2.65, 1.25, 1.45]} material={palette.secondary} position={[-1.05, 1.48, 0]} />
    {[-0.65, 0.1].map((x) => <Box key={x} args={[0.05, 0.92, 1.48]} material={palette.edge} position={[x, 1.4, 0]} />)}
    <Box args={[2.3, 0.12, 1.1]} material={palette.accent} position={[-1.05, 2.15, 0]} />
  </group>;
};

const MotorGraderBody = () => {
  const palette = useBodyPalette('motor_grader');
  return <group>
    <StandardFrame palette={palette} length={7.8} width={1.05} />
    <Cab palette={palette} position={[-0.45, 1.75, 0]} size={[1.35, 1.62, 1.25]} />
    <Box args={[2.2, 0.62, 1.08]} material={palette.body} position={[-2.15, 1.15, 0]} />
    <Boom palette={palette} from={[-0.2, 0.94]} to={[2.95, 1.08]} thickness={0.22} />
    <Box args={[0.28, 0.68, 2.5]} material={palette.secondary} position={[0.85, 0.48, 0]} rotation={[0.08, 0.1, -0.18]} />
  </group>;
};

const ForkliftBody = () => {
  const palette = useBodyPalette('forklift');
  return <group>
    <StandardFrame palette={palette} length={3.7} width={1.25} />
    <Cab palette={palette} position={[-0.45, 1.58, 0]} size={[1.2, 1.45, 1.2]} />
    <Box args={[1.0, 0.9, 1.3]} material={palette.body} position={[-1.35, 1.15, 0]} />
    {[-0.5, 0.5].map((z) => <Box key={z} args={[0.16, 2.45, 0.12]} material={palette.edge} position={[1.35, 1.75, z]} />)}
    {[-0.45, 0.45].map((z) => <Box key={z} args={[1.55, 0.1, 0.13]} material={palette.edge} position={[2.0, 0.34, z]} />)}
  </group>;
};

const TelehandlerBody = () => {
  const palette = useBodyPalette('telehandler');
  return <group>
    <StandardFrame palette={palette} length={5.4} />
    <Cab palette={palette} position={[-1.0, 1.58, 0.42]} size={[1.35, 1.5, 0.75]} />
    <Box args={[2.15, 0.62, 1.25]} material={palette.body} position={[-0.7, 1.12, 0]} />
    <Boom palette={palette} from={[-0.75, 1.55]} to={[2.65, 2.35]} thickness={0.28} depth={0.34} />
    <Box args={[0.14, 1.45, 1.42]} material={palette.edge} position={[2.68, 1.64, 0]} />
  </group>;
};

const TractorBody = () => {
  const palette = useBodyPalette('tractor');
  return <group>
    <StandardFrame palette={palette} length={4.6} width={1.1} />
    <Cab palette={palette} position={[-0.9, 1.66, 0]} size={[1.35, 1.55, 1.25]} />
    <Box args={[2.1, 0.86, 1.0]} material={palette.body} position={[1.0, 1.22, 0]} />
    <Cylinder args={[0.09, 0.09, 1.08, 14]} material={palette.edge} position={[0.4, 2.12, 0.35]} rotation={[0, 0, 0]} />
    <Box args={[0.8, 0.16, 0.9]} material={palette.edge} position={[-2.45, 0.82, 0]} />
  </group>;
};

const UndergroundLhdBody = () => {
  const palette = useBodyPalette('underground_lhd');
  return <group>
    <StandardFrame palette={palette} length={6.2} width={1.5} />
    <Cab palette={palette} position={[-1.2, 1.38, 0.42]} size={[1.55, 1.0, 0.72]} />
    <Box args={[2.0, 0.82, 1.45]} material={palette.body} position={[-1.2, 1.14, 0]} />
    <Boom palette={palette} from={[0.0, 1.15]} to={[2.45, 0.7]} thickness={0.24} depth={0.3} />
    <Box args={[0.9, 0.55, 2.0]} material={palette.body} position={[2.75, 0.48, 0]} rotation={[0, 0, -0.12]} />
  </group>;
};

const UndergroundDumperBody = () => {
  const palette = useBodyPalette('underground_dumper');
  return <group>
    <StandardFrame palette={palette} length={6.8} width={1.5} />
    <Cab palette={palette} position={[2.25, 1.36, 0.38]} size={[1.45, 1.04, 0.75]} />
    <Box args={[3.55, 0.28, 1.62]} material={palette.body} position={[-0.95, 1.05, 0]} rotation={[0, 0, -0.08]} />
    <Box args={[2.9, 1.0, 0.16]} material={palette.secondary} position={[-1.05, 1.46, 0.74]} rotation={[0, 0, -0.08]} />
    <Box args={[2.9, 1.0, 0.16]} material={palette.secondary} position={[-1.05, 1.46, -0.74]} rotation={[0, 0, -0.08]} />
  </group>;
};

const WheeledExcavatorBody = () => {
  const palette = useBodyPalette('wheeled_excavator');
  return <group>
    <StandardFrame palette={palette} length={5.4} width={1.45} />
    <Cylinder args={[1.15, 1.15, 0.28, 24]} material={palette.chassis} position={[-0.6, 1.0, 0]} rotation={[Math.PI / 2, 0, 0]} />
    <Cab palette={palette} position={[-1.05, 1.75, 0.38]} size={[1.5, 1.5, 0.75]} />
    <Boom palette={palette} from={[-0.1, 1.65]} to={[1.55, 3.0]} thickness={0.25} depth={0.28} />
    <Boom palette={palette} from={[1.55, 3.0]} to={[2.85, 1.0]} thickness={0.2} depth={0.24} />
    <Box args={[0.72, 0.5, 1.25]} material={palette.body} position={[3.0, 0.62, 0]} rotation={[0, 0, -0.2]} />
  </group>;
};

const WheelDozerBody = () => {
  const palette = useBodyPalette('wheel_dozer');
  return <group>
    <StandardFrame palette={palette} length={5.2} width={1.5} />
    <Cab palette={palette} position={[-0.8, 1.7, 0]} size={[1.5, 1.55, 1.35]} />
    <Box args={[2.0, 0.72, 1.3]} material={palette.body} position={[0.8, 1.2, 0]} />
    <Box args={[0.22, 1.25, 2.45]} material={palette.secondary} position={[2.75, 0.72, 0]} rotation={[0, 0, -0.12]} />
    {[-1, 1].map((side) => <Boom
      key={side}
      palette={palette}
      from={[0.75, 0.92]}
      to={[2.55, 0.74]}
      thickness={0.16}
      depth={0.16}
      z={side * 0.68}
    />)}
  </group>;
};

const WheeledDrillRigBody = () => {
  const palette = useBodyPalette('wheeled_drill_rig');
  return <group>
    <StandardFrame palette={palette} length={6.4} width={1.4} />
    <Cab palette={palette} position={[-1.9, 1.58, 0]} />
    <Box args={[2.1, 0.82, 1.35]} material={palette.body} position={[0.1, 1.2, 0]} />
    <Box args={[0.3, 4.7, 0.42]} material={palette.secondary} position={[1.55, 2.9, 0]} rotation={[0, 0, -0.08]} />
    <Cylinder args={[0.1, 0.1, 2.8, 12]} material={palette.edge} position={[1.75, 1.35, 0]} rotation={[0, 0, -0.08]} />
  </group>;
};

const WheeledCraneBody = () => {
  const palette = useBodyPalette('wheeled_crane');
  return <group>
    <StandardFrame palette={palette} length={7.0} width={1.4} />
    <Cab palette={palette} position={[2.25, 1.5, 0]} />
    <Box args={[2.0, 0.7, 1.4]} material={palette.body} position={[-1.35, 1.16, 0]} />
    <Boom palette={palette} from={[-1.1, 1.55]} to={[2.0, 4.15]} thickness={0.3} depth={0.3} />
    <Cylinder args={[0.06, 0.06, 2.2, 10]} material={palette.edge} position={[2.0, 3.0, 0]} rotation={[0, 0, 0]} />
    <Box args={[0.28, 0.28, 0.28]} material={palette.accent} position={[2.0, 1.9, 0]} />
  </group>;
};

const PneumaticTyreRollerBody = () => {
  const palette = useBodyPalette('pneumatic_tyre_roller');
  return <group>
    <StandardFrame palette={palette} length={4.8} width={1.65} />
    <Cab palette={palette} position={[-0.4, 1.72, 0]} size={[1.45, 1.55, 1.45]} />
    <Box args={[1.75, 0.72, 1.7]} material={palette.body} position={[1.15, 1.15, 0]} />
    <Box args={[4.45, 0.18, 1.8]} material={palette.secondary} position={[0, 0.92, 0]} />
    <Box args={[1.1, 0.26, 1.74]} material={palette.edge} position={[-1.75, 0.58, 0]} />
  </group>;
};

export {
  ForkliftBody,
  FuelBowserBody,
  MotorGraderBody,
  PneumaticTyreRollerBody,
  ServiceVehicleBody,
  TelehandlerBody,
  TractorBody,
  UndergroundDumperBody,
  UndergroundLhdBody,
  WheelDozerBody,
  WheeledCraneBody,
  WheeledDrillRigBody,
  WheeledExcavatorBody,
};
